import * as React from 'react';
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    Grid,
    FormHelperText,
} from '@mui/material';
import pxToRem from 'assets/theme/function/px-to-rem';
import borders from 'assets/theme/base/borders';
import colors from 'assets/theme/base/colors';
import rgba from 'assets/theme/function/rgba';
import { Controller, useForm } from 'react-hook-form';
import { CreatDraftProduct, ProductResponse } from 'types/product/product';
import { API_ENDPOINT_URL } from 'constant';
import {
    useUpdateDraftProductMutation,
    useUpdateActiveProductMutation,
} from 'store/slices/product/product-list';
import { useEffect } from 'react';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { useAppDispatch } from 'hooks/redux';
import { ImageTypeEnum } from 'types/card/card';
import { useRuleProductListQuery } from 'store/slices/rule/rule-entities-enum';
import {
    useProductDisplayTypeQuery,
    useProductTypeQuery,
} from 'store/slices/product/product-enum';
import BackNavigate from 'component/back-navigate/back-navigate';

interface Props {
    productCategoryList?: string[];
    creatProduct: any;
    getProductId: any;
    productId?: string;
    productData: ProductResponse | undefined;
    viewProduct?: string;
}
const AddProductDetail: React.FC<Props> = (props: Props) => {
    const {
        productCategoryList,
        creatProduct,
        productId,
        productData,
        viewProduct,
    } = props;
    const {
        handleSubmit,
        control,
        formState: { errors },
    } = useForm<CreatDraftProduct>();

    const { data: displayTypeData } = useProductDisplayTypeQuery();
    const { data: productType } = useProductTypeQuery();
    const { data: ruleProductList } = useRuleProductListQuery({
        entity: 'product',
    });

    const [
        updateDraftProduct,
        {
            error: updateDraftError,
            isError: updateDraftIsError,
            isSuccess: updateDraftIsSuccess,
        },
    ] = useUpdateDraftProductMutation();

    const [
        updateActiveProduct,
        {
            error: updateActiveError,
            isError: updateActiveIsError,
            isSuccess: updateActiveIsSuccess,
        },
    ] = useUpdateActiveProductMutation();

    const dispatch = useAppDispatch();

    const onSubmit = (values: CreatDraftProduct) => {
        if (productId && productData?.status === 'draft') {
            updateDraftProduct({
                ...values,
                productId: productId,
                position: Number(values.position),
            });
        } else if (productId && productData?.status === 'active') {
            updateActiveProduct({
                ...values,
                productId: productId,
                position: Number(values.position),
            });
        } else {
            creatProduct({ ...values, position: Number(values.position) });
        }
    };

    useEffect(() => {
        const error = updateDraftError || updateActiveError;
        if (error && 'data' in error) {
            dispatch(
                openSnackbar({
                    message: error?.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }

        if (updateDraftIsSuccess || updateActiveIsSuccess) {
            dispatch(
                openSnackbar({
                    message: updateDraftIsSuccess
                        ? 'Product Draft Updates'
                        : 'Product Active Update',
                    severity: 'info',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [
        updateDraftIsSuccess,
        updateActiveIsSuccess,
        updateActiveIsError,
        updateDraftIsError,
    ]);

    return (
        <Box
            sx={{
                pointerEvents: viewProduct ? 'none' : 'auto',
                opacity: viewProduct ? 0.5 : 1,
            }}
        >
            <Box mb={pxToRem(10)} display={'grid'} alignItems={'center'}>
                {!viewProduct?.length && (
                    <>
                        <BackNavigate navigateUrl={'/product/products'} />
                        <Typography variant="h5" pl={pxToRem(10)}>
                            {productData ? 'Edit Product' : 'Add Product'}
                        </Typography>
                    </>
                )}
            </Box>
            <Box
                py={pxToRem(20)}
                px={pxToRem(10)}
                mt={pxToRem(5)}
                borderRadius={borders.borderRadius.xl}
                bgcolor={colors.secondary.main}
                border={`1px solid ${rgba(colors.info.main, 0.5)}`}
                component="form"
                onSubmit={handleSubmit(onSubmit)}
            >
                <Grid container spacing={2}>
                    {productData?.fileId && (
                        <Grid size={1}>
                            <img
                                src={`${API_ENDPOINT_URL}/file/file/${productData?.fileId}?source=${ImageTypeEnum.PRODUCT}`}
                                alt="Product Image"
                                width={80}
                                height={100}
                            />
                        </Grid>
                    )}
                    <Grid size={productData?.fileId ? 11 : 12}>
                        <Box display="flex" gap={2}>
                            <Controller
                                name="title"
                                control={control}
                                defaultValue={productData?.productTitle || ''}
                                rules={{
                                    required: 'Product Title is required',
                                }}
                                render={({
                                    field: { name, value, onChange },
                                    fieldState: { error },
                                }) => {
                                    useEffect(() => {
                                        if (productData?.productTitle) {
                                            onChange(productData.productTitle);
                                        }
                                    }, [productData?.productTitle]);

                                    return (
                                        <TextField
                                            fullWidth
                                            name={name}
                                            value={value || ''}
                                            onChange={onChange}
                                            label="Title"
                                            error={!!error}
                                            helperText={error?.message || ''}
                                        />
                                    );
                                }}
                            />
                            <FormControl
                                error={Boolean(errors.productCategory)}
                                fullWidth
                            >
                                <InputLabel
                                    disabled={
                                        !!productId &&
                                        productData?.status !== 'draft'
                                    }
                                >
                                    Product Category
                                </InputLabel>
                                <Controller
                                    name="productCategory"
                                    control={control}
                                    rules={{
                                        required:
                                            'Product Category  is required',
                                    }}
                                    defaultValue={
                                        productData?.productCategory || ''
                                    }
                                    render={({
                                        field: { name, value, onChange },
                                    }) => {
                                        useEffect(() => {
                                            if (productData?.productCategory) {
                                                onChange(
                                                    productData.productCategory,
                                                );
                                            }
                                        }, [productData?.productCategory]);

                                        return (
                                            <Select
                                                disabled={
                                                    !!productId &&
                                                    productData?.status !==
                                                        'draft'
                                                }
                                                label="Product Category"
                                                name={name}
                                                value={value ?? ''}
                                                onChange={(e) => {
                                                    onChange(e.target.value);
                                                }}
                                            >
                                                {productCategoryList?.map(
                                                    (category) => (
                                                        <MenuItem
                                                            key={category.toString()}
                                                            value={category.toString()}
                                                        >
                                                            {category}
                                                        </MenuItem>
                                                    ),
                                                )}
                                            </Select>
                                        );
                                    }}
                                />
                                {errors.productCategory && (
                                    <FormHelperText>
                                        {errors.productCategory.message?.toString()}
                                    </FormHelperText>
                                )}
                            </FormControl>

                            <Controller
                                name="productNumber"
                                control={control}
                                defaultValue={productData?.productNumber || ''}
                                rules={{
                                    required: 'Product Number is required',
                                }}
                                render={({
                                    field: { name, value, onChange },
                                    fieldState: { error },
                                }) => {
                                    useEffect(() => {
                                        if (productData?.productNumber) {
                                            onChange(productData.productNumber);
                                        }
                                    }, [productData?.productNumber]);

                                    return (
                                        <TextField
                                            fullWidth
                                            name={name}
                                            value={value || ''}
                                            onChange={onChange}
                                            label="Product Number"
                                            error={!!error}
                                            helperText={error?.message || ''}
                                        />
                                    );
                                }}
                            />

                            <Controller
                                name="position"
                                control={control}
                                rules={{
                                    required: 'Product number is required',
                                    pattern: {
                                        value: /^[0-9]*[.,]?[0-9]+$/,
                                        message: 'Only numbers are allowed',
                                    },
                                }}
                                render={({ field, fieldState: { error } }) => {
                                    useEffect(() => {
                                        if (productData?.position) {
                                            field.onChange(
                                                productData.position,
                                            );
                                        }
                                    }, [productData?.position]);

                                    return (
                                        <TextField
                                            fullWidth
                                            label="Position"
                                            {...field}
                                            value={
                                                field.value ||
                                                productData?.position ||
                                                ''
                                            }
                                            error={!!error}
                                            helperText={error?.message || ''}
                                            slotProps={{
                                                input: {
                                                    inputMode: 'numeric',
                                                    onInput: (
                                                        e: React.FormEvent<HTMLInputElement>,
                                                    ) => {
                                                        const value = (
                                                            e.target as HTMLInputElement
                                                        ).value;
                                                        if (
                                                            !/^\d+$/.test(value)
                                                        ) {
                                                            e.preventDefault();
                                                        }
                                                    },
                                                },
                                            }}
                                        />
                                    );
                                }}
                            />

                            <FormControl
                                error={Boolean(errors.displayType)}
                                fullWidth
                            >
                                <InputLabel
                                    disabled={
                                        !!productId &&
                                        productData?.status !== 'draft'
                                    }
                                >
                                    Display Type
                                </InputLabel>
                                <Controller
                                    name="displayType"
                                    control={control}
                                    rules={{
                                        required: 'Display Type is required',
                                    }}
                                    defaultValue={
                                        productData?.displayType || ''
                                    }
                                    render={({
                                        field: { name, value, onChange },
                                    }) => {
                                        useEffect(() => {
                                            if (productData?.displayType) {
                                                onChange(
                                                    productData.displayType,
                                                );
                                            }
                                        }, [productData?.displayType]);

                                        return (
                                            <Select
                                                disabled={
                                                    !!productId &&
                                                    productData?.status !==
                                                        'draft'
                                                }
                                                label="Dispaly Type"
                                                name={name}
                                                value={value ?? ''}
                                                onChange={(e) => {
                                                    onChange(e.target.value);
                                                }}
                                            >
                                                {displayTypeData?.map(
                                                    (category) => (
                                                        <MenuItem
                                                            key={category.toString()}
                                                            value={category.toString()}
                                                        >
                                                            {category}
                                                        </MenuItem>
                                                    ),
                                                )}
                                            </Select>
                                        );
                                    }}
                                />
                                {errors.displayType && (
                                    <FormHelperText>
                                        {errors.displayType.message?.toString()}
                                    </FormHelperText>
                                )}
                            </FormControl>

                            <FormControl
                                error={Boolean(errors.productType)}
                                fullWidth
                            >
                                <InputLabel
                                    disabled={
                                        !!productId &&
                                        productData?.status !== 'draft'
                                    }
                                >
                                    Product Type
                                </InputLabel>
                                <Controller
                                    name="productType"
                                    control={control}
                                    rules={{
                                        required: 'Product Type is required',
                                    }}
                                    defaultValue={
                                        productData?.productType || ''
                                    }
                                    render={({
                                        field: { name, value, onChange },
                                    }) => {
                                        useEffect(() => {
                                            if (productData?.productType) {
                                                onChange(
                                                    productData.productType,
                                                );
                                            }
                                        }, [productData?.productType]);

                                        return (
                                            <Select
                                                disabled={
                                                    !!productId &&
                                                    productData?.status !==
                                                        'draft'
                                                }
                                                label="Product Type"
                                                name={name}
                                                value={value ?? ''}
                                                onChange={(e) => {
                                                    onChange(e.target.value);
                                                }}
                                            >
                                                {productType?.map(
                                                    (category) => (
                                                        <MenuItem
                                                            key={category.toString()}
                                                            value={category.toString()}
                                                        >
                                                            {category}
                                                        </MenuItem>
                                                    ),
                                                )}
                                            </Select>
                                        );
                                    }}
                                />
                                {errors.productType && (
                                    <FormHelperText>
                                        {errors.productType.message?.toString()}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Box>
                    </Grid>

                    <Box
                        display="flex"
                        alignItems={'center'}
                        justifyContent={'space-between'}
                        width={'100%'}
                        pt={pxToRem(5)}
                    >
                        <FormControl sx={{ width: '33%' }}>
                            <InputLabel
                                disabled={
                                    !!productId &&
                                    productData?.status !== 'draft'
                                }
                            >
                                Rule
                            </InputLabel>
                            <Controller
                                name="ruleId"
                                control={control}
                                defaultValue={productData?.ruleId}
                                render={({
                                    field: { name, value, onChange },
                                }) => {
                                    useEffect(() => {
                                        if (productData?.ruleId) {
                                            onChange(productData.ruleId);
                                        }
                                    }, [productData?.ruleId]);

                                    return (
                                        <Select
                                            disabled={
                                                !!productId &&
                                                productData?.status !== 'draft'
                                            }
                                            label="Rule"
                                            name={name}
                                            value={value ?? ''}
                                            onChange={(e) => {
                                                onChange(e.target.value);
                                            }}
                                        >
                                            {ruleProductList?.map(
                                                (category) => (
                                                    <MenuItem
                                                        key={category.id.toString()}
                                                        value={category.id.toString()}
                                                    >
                                                        {category.name}
                                                    </MenuItem>
                                                ),
                                            )}
                                        </Select>
                                    );
                                }}
                            />
                        </FormControl>
                        <Button variant="contained" type="submit">
                            {productId ? 'Update Product' : 'Creat Product'}
                        </Button>
                    </Box>
                </Grid>
            </Box>
        </Box>
    );
};

export default AddProductDetail;
